'use client';
import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

type EnquiryType = 'golf_day' | 'group_booking' | 'corporate' | 'general';

interface ContactFormData {
  enquiryType: EnquiryType;
  name: string;
  email: string;
  phone: string;
  organisation: string;
  preferredDate: string;
  playerCount: string;
  message: string;
}

const enquiryOptions: { value: EnquiryType; label: string; icon: string; description: string }[] = [
  {
    value: 'golf_day',
    label: 'Golf Day',
    icon: 'TrophyIcon',
    description: 'Full golf day event for your company or society',
  },
  {
    value: 'group_booking',
    label: 'Group Booking',
    icon: 'UserGroupIcon',
    description: '5+ players needing consecutive tee times',
  },
  {
    value: 'corporate',
    label: 'Corporate Package',
    icon: 'BuildingOfficeIcon',
    description: 'Tailored corporate hospitality & entertainment',
  },
  {
    value: 'general',
    label: 'General Enquiry',
    icon: 'ChatBubbleLeftRightIcon',
    description: 'Any other question or request',
  },
];

const contactDetails = [
  {
    icon: 'PhoneIcon',
    label: 'Pro Shop',
    value: '033 396 2356',
    href: 'tel:0333962356',
  },
  {
    icon: 'EnvelopeIcon',
    label: 'Email',
    value: 'golf@maritzburggolfclub.co.za',
    href: 'mailto:golf@maritzburggolfclub.co.za',
  },
  {
    icon: 'MapPinIcon',
    label: 'Address',
    value: 'New England Rd, Hayfields, PMB, 3201',
    href: 'https://maps.google.com/?q=Maritzburg+Golf+Club',
  },
  {
    icon: 'ClockIcon',
    label: 'Hours',
    value: 'Mon–Fri 6:30am–5pm · Sat–Sun 6:00am–5pm',
    href: null,
  },
];

export default function ContactPage() {
  const [form, setForm] = useState<ContactFormData>({
    enquiryType: 'golf_day',
    name: '',
    email: '',
    phone: '',
    organisation: '',
    preferredDate: '',
    playerCount: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEnquiryType = (type: EnquiryType) => {
    setForm((prev) => ({ ...prev, enquiryType: type }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.error || 'Submission failed');
      }

      setStatus('success');
      setForm({
        enquiryType: 'golf_day',
        name: '',
        email: '',
        phone: '',
        organisation: '',
        preferredDate: '',
        playerCount: '',
        message: '',
      });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const isGroupType = form.enquiryType === 'golf_day' || form.enquiryType === 'group_booking' || form.enquiryType === 'corporate';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative bg-primary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/30 via-transparent to-secondary/20" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-16 md:py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-accent" />
              <span className="text-accent text-xs font-semibold tracking-widest uppercase">Get in Touch</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
              Golf Days &amp; Group Enquiries
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
              Planning a corporate golf day, society outing, or group booking? Our team will tailor the perfect experience on South Africa&apos;s oldest playing course.
            </p>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12 md:py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">

          {/* Left: Contact Info */}
          <aside className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-5">Contact Details</h2>
              <ul className="space-y-4">
                {contactDetails.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon name={item.icon} size={17} variant="outline" className="text-primary" />
                    </span>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-sm text-foreground font-medium hover:text-primary transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-sm text-foreground font-medium">{item.value}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick book CTA */}
            <div className="bg-primary rounded-xl p-6">
              <h3 className="text-white font-bold text-base mb-2">Ready to book a round?</h3>
              <p className="text-white/60 text-sm mb-4 leading-relaxed">
                For individual tee times, use our instant online booking system.
              </p>
              <Link
                href="/online-booking"
                className="btn-gold w-full text-center px-4 py-2.5 rounded-lg text-sm font-semibold block"
              >
                Book Online Now
              </Link>
            </div>

            {/* Response time note */}
            <div className="border border-border rounded-xl p-5 bg-card">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ClockIcon" size={15} variant="outline" className="text-accent" />
                <span className="text-sm font-semibold text-foreground">Response Time</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We aim to respond to all enquiries within <strong className="text-foreground">1 business day</strong>. For urgent matters, call the pro shop directly.
              </p>
            </div>
          </aside>

          {/* Right: Form */}
          <div className="lg:col-span-2">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                  <Icon name="CheckCircleIcon" size={36} variant="outline" className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Enquiry Sent!</h2>
                <p className="text-muted-foreground text-base max-w-md leading-relaxed mb-8">
                  Thank you for reaching out. Our team will review your enquiry and get back to you within 1 business day.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={() => setStatus('idle')}
                    className="px-5 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    Send Another Enquiry
                  </button>
                  <Link
                    href="/online-booking"
                    className="btn-gold px-5 py-2.5 rounded-lg text-sm font-semibold"
                  >
                    Book a Round
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-7">
                {/* Enquiry Type */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    What are you enquiring about? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {enquiryOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleEnquiryType(opt.value)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-center transition-all duration-150 ${
                          form.enquiryType === opt.value
                            ? 'border-primary bg-primary/5 text-primary' :'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        <Icon
                          name={opt.icon}
                          size={22}
                          variant="outline"
                          className={form.enquiryType === opt.value ? 'text-primary' : 'text-muted-foreground'}
                        />
                        <span className="text-xs font-semibold leading-tight">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {enquiryOptions.find((o) => o.value === form.enquiryType)?.description}
                  </p>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                {/* Phone + Organisation */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1.5">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="e.g. 082 123 4567"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="organisation" className="block text-sm font-medium text-foreground mb-1.5">
                      {form.enquiryType === 'general' ? 'Organisation (optional)' : 'Company / Organisation'}
                    </label>
                    <input
                      id="organisation"
                      name="organisation"
                      type="text"
                      value={form.organisation}
                      onChange={handleChange}
                      placeholder="Your company or club name"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                {/* Date + Players (for group types) */}
                {isGroupType && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="preferredDate" className="block text-sm font-medium text-foreground mb-1.5">
                        Preferred Date
                      </label>
                      <input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={form.preferredDate}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="playerCount" className="block text-sm font-medium text-foreground mb-1.5">
                        Number of Players
                      </label>
                      <select
                        id="playerCount"
                        name="playerCount"
                        value={form.playerCount}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      >
                        <option value="">Select range</option>
                        <option value="5-8">5 – 8 players</option>
                        <option value="9-16">9 – 16 players</option>
                        <option value="17-24">17 – 24 players</option>
                        <option value="25-32">25 – 32 players</option>
                        <option value="33-40">33 – 40 players</option>
                        <option value="40+">40+ players</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder={
                      form.enquiryType === 'golf_day' ?'Tell us about your golf day — format, catering needs, prizes, budget...'
                        : form.enquiryType === 'group_booking' ?'Describe your group, preferred tee times, any special requirements...'
                        : form.enquiryType === 'corporate' ?'Describe your corporate event requirements, budget, and expectations...' :'How can we help you?'
                    }
                    className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                  />
                </div>

                {/* Error */}
                {status === 'error' && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
                    <Icon name="ExclamationCircleIcon" size={18} variant="outline" className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}

                {/* Submit */}
                <div className="flex items-center justify-between gap-4 pt-1">
                  <p className="text-xs text-muted-foreground">
                    We&apos;ll respond within 1 business day.
                  </p>
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="btn-gold px-7 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      <>
                        <Icon name="PaperAirplaneIcon" size={16} variant="outline" />
                        Send Enquiry
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
